import {Button} from "primereact/button";
import {InputText} from "primereact/inputtext";
import {useTranslation} from "react-i18next";

import {isValidEmail} from "../../util/emailUtil";
import {type Contact, hasContactWay, isValidPhone, MAX_CONTACT_NAME_LENGTH} from "./commissionOrderUtil";

interface ContactFieldsProps {
    contact: Contact;
    onChange: (name: keyof Contact, value: string) => void;
    disabled: boolean;
    idPrefix: string;
    showInvalid: boolean;
}

export function ContactFields({contact, onChange, disabled, idPrefix, showInvalid}: ContactFieldsProps) {
    const {t} = useTranslation();
    const contactWayMissing = showInvalid && !hasContactWay(contact);

    const field = (name: keyof Contact, required = false, type = "text") => {
        const invalid =
            (required && showInvalid && !contact[name]) ||
            (name === "phoneMobile" && !!contact.phoneMobile && !isValidPhone(contact.phoneMobile)) ||
            (name === "email" && !!contact.email && !isValidEmail(contact.email)) ||
            ((name === "phoneMobile" || name === "email") && contactWayMissing);
        return (
            <div className="field col-span-6">
                <label htmlFor={`${idPrefix}-${name}`}>
                    {t(`commissionOrderDialog.contact.${name}`)}
                    {required && <span className="commission-required">*</span>}
                </label>
                <InputText
                    id={`${idPrefix}-${name}`}
                    className={`w-full p-inputtext-sm${invalid ? " p-invalid" : ""}`}
                    type={type}
                    value={contact[name] ?? ""}
                    maxLength={name === "firstName" || name === "lastName" ? MAX_CONTACT_NAME_LENGTH : undefined}
                    onChange={(e) => onChange(name, e.target.value)}
                    disabled={disabled}
                />
            </div>
        );
    };
    return (
        <div className="formgrid commission-grid">
            {field("firstName")}
            {field("lastName", true)}
            {field("phoneMobile")}
            {field("email", false, "email")}
            <div className="field col-span-12 !mb-0">
                <p className={`text-sm mt-0 mb-0${contactWayMissing ? " text-red-500" : " text-color-secondary"}`}>
                    {t("commissionOrderDialog.contact.phoneOrEmailHint")}
                </p>
            </div>
        </div>
    );
}

interface ContactSectionProps {
    additionalContacts: Contact[];
    onAdditionalContactChange: (index: number, name: keyof Contact, value: string) => void;
    onAddContact: () => void;
    onRemoveContact: (index: number) => void;
    disabled: boolean;
    showInvalid: boolean;
}

export function ContactSection({
    additionalContacts,
    onAdditionalContactChange,
    onAddContact,
    onRemoveContact,
    disabled,
    showInvalid,
}: ContactSectionProps) {
    const {t} = useTranslation();
    return (
        <>
            {additionalContacts.map((contact, index) => (
                // biome-ignore lint/suspicious/noArrayIndexKey: rows have no identity of their own, values are fully controlled from `additionalContacts`
                <div key={index} className="commission-panel">
                    <div className="flex items-center justify-between mb-4">
                        <span className="font-bold">{t("commissionOrderDialog.contact.additional")}</span>
                        <Button
                            icon="pi pi-times"
                            className="p-button-text p-button-sm"
                            disabled={disabled}
                            onClick={() => onRemoveContact(index)}
                        />
                    </div>
                    <ContactFields
                        contact={contact}
                        onChange={(name, value) => onAdditionalContactChange(index, name, value)}
                        disabled={disabled}
                        idPrefix={`additional-${index}`}
                        showInvalid={showInvalid}
                    />
                </div>
            ))}

            <Button
                label={t("commissionOrderDialog.contact.add")}
                icon="pi pi-plus"
                className="p-button-text w-full"
                disabled={disabled}
                onClick={onAddContact}
            />
        </>
    );
}
